/**
 * TrainingSession — Guided training wizard with embedded forms.
 *
 * Left: vertical step list with green/blue/grey indicators.
 * Right: instructions panel + action area for current step.
 *
 * Steps 1-2: link to real DocEditor, "Check Progress" validates.
 * Steps 3-4: inline COI + qualification form, calls /@docs/draft/submit.
 * Steps 5-6: inline FRM-DC-004 checklist + approve form, calls /@docs/draft/approve.
 *
 * Route: /qms/training/session/:sessionId
 * API: GET /@qms/training/session, POST /@qms/training/session/validate-step,
 *      POST /@docs/draft/submit, POST /@docs/draft/approve
 *
 * Ref: CR-2026-027
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Grid,
  Step,
  Icon,
  Loader,
  Button,
  Message,
  Form,
  TextArea,
  Checkbox,
  Dropdown,
  Table,
  Divider,
  Label,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../../api';

// FRM-DC-004 Self-Review Checklist (same template as DocDraftReview)
const CHECKLIST_TEMPLATE = {
  A: {
    title: 'A. Document Identification',
    items: [
      { id: 'A1', text: 'Document ID is correct and follows naming convention' },
      { id: 'A2', text: 'Title accurately describes the document content' },
      { id: 'A3', text: 'Version number is correct and follows versioning policy' },
      { id: 'A4', text: 'Document type and domain are correctly assigned' },
      { id: 'A5', text: 'Author, owner, and reviewer fields are populated' },
    ],
  },
  B: {
    title: 'B. Content Quality',
    items: [
      { id: 'B1', text: 'Content is technically accurate and complete' },
      { id: 'B2', text: 'Language is clear, unambiguous, and appropriate' },
      { id: 'B3', text: 'All referenced documents and standards are correct' },
      { id: 'B4', text: 'Formatting is consistent throughout the document' },
      { id: 'B5', text: 'No placeholder or draft-only content remains' },
    ],
  },
  C: {
    title: 'C. Regulatory Compliance',
    items: [
      { id: 'C1', text: 'Meets applicable regulatory requirements (EU MDR, ISO 13485)' },
      { id: 'C2', text: 'Traceability to source requirements is maintained' },
      { id: 'C3', text: 'Risk assessment references are current' },
      { id: 'C4', text: 'Retention and archival requirements are specified' },
    ],
  },
  D: {
    title: 'D. Process Compliance',
    items: [
      { id: 'D1', text: 'Change was initiated with proper justification' },
      { id: 'D2', text: 'COI declarations have been recorded' },
      { id: 'D3', text: 'All affected processes and documents are identified' },
      { id: 'D4', text: 'Training requirements from this change are identified' },
    ],
  },
  E: {
    title: 'E. Integration',
    items: [
      { id: 'E1', text: 'No conflicts with other QMS documents' },
      { id: 'E2', text: 'Cross-references to other documents are valid' },
      { id: 'E3', text: 'Effective date and review schedule are appropriate' },
    ],
  },
};

const RESULT_OPTIONS = [
  { key: 'pass', value: 'pass', text: 'Pass' },
  { key: 'fail', value: 'fail', text: 'Fail' },
  { key: 'na', value: 'na', text: 'N/A' },
];

const CLASSIFICATION_OPTIONS = [
  { key: 'MAJOR', value: 'MAJOR', text: 'MAJOR — new version' },
  { key: 'MINOR', value: 'MINOR', text: 'MINOR — additive change' },
  { key: 'PATCH', value: 'PATCH', text: 'PATCH — editorial/correction' },
];

export default function TrainingSession() {
  const { sessionId } = useParams();
  const history = useHistory();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationMsg, setValidationMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [abandoning, setAbandoning] = useState(false);

  // COI + qualification form state (steps 3-4)
  const [coiHasConflict, setCoiHasConflict] = useState(false);
  const [coiDeclaration, setCoiDeclaration] = useState('');
  const [microEnterprise, setMicroEnterprise] = useState(true);
  const [qualificationBasis, setQualificationBasis] = useState('');

  // Approval form state (steps 5-6)
  const [checklistResults, setChecklistResults] = useState({});
  const [checklistNotes, setChecklistNotes] = useState({});
  const [changeClassification, setChangeClassification] = useState('PATCH');
  const [classificationRationale, setClassificationRationale] = useState('');
  const [approvalJustification, setApprovalJustification] = useState('');
  const [aiAssisted, setAiAssisted] = useState(false);
  const [aiDisclosure, setAiDisclosure] = useState('');

  const loadSession = useCallback(async () => {
    try {
      const allSessions = await fetchApi(`${getApiPath()}/@qms/training/session`);
      const items = allSessions.items || [];
      const found = items.find((s) => String(s.session_id) === String(sessionId));
      if (!found) {
        setError('Training session not found');
        setLoading(false);
        return;
      }
      setSession(found);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const steps = session?.module_steps || [];
  const completedSteps = session?.completed_steps || [];
  const completedNums = new Set(
    (Array.isArray(completedSteps) ? completedSteps : []).map((s) => s.step_number),
  );
  const currentStep = session?.current_step || 1;

  const getStepSlug = () => {
    if (!session?.training_doc_id) return '';
    return `training/${session.training_doc_id}`;
  };

  const handleValidateStep = async (stepNum) => {
    setValidating(true);
    setValidationMsg(null);
    setError(null);
    try {
      const res = await fetch(
        `${getApiPath()}/@qms/training/session/validate-step`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            session_id: parseInt(sessionId, 10),
            step_number: stepNum,
          }),
        },
      );
      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      const json = await res.json();
      if (json.ok) {
        setValidationMsg({ positive: true, text: json.message });
        if (json.completed) {
          // Full completion — reload session to show certificate
          setTimeout(() => loadSession(), 500);
        } else {
          // Advance to next step — reload session
          await loadSession();
        }
      } else {
        setValidationMsg({ positive: false, text: json.message });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const slug = getStepSlug();
      const payload = {
        slug,
        coi_declaration: {
          has_conflict: coiHasConflict,
          declaration: coiDeclaration || 'No conflicts declared',
          micro_enterprise_provision: microEnterprise,
          same_author_reviewer: true,
          mitigation: microEnterprise
            ? 'Micro-enterprise provision per SOP-DC-001 §10.3'
            : '',
        },
        qualification: {
          basis: qualificationBasis || 'Training exercise — demonstrating competence',
          contributors: [],
        },
      };

      const res = await fetch(`${getApiPath()}/@docs/draft/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status}`);
      }

      // Now validate steps 3 and 4
      await handleValidateStep(3);
      // After step 3 advances, validate step 4
      setTimeout(async () => {
        await handleValidateStep(4);
      }, 300);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const allChecklistAnswered = Object.values(CHECKLIST_TEMPLATE).every(
    (section) =>
      section.items.every(
        (item) =>
          checklistResults[item.id] &&
          ['pass', 'fail', 'na'].includes(checklistResults[item.id]),
      ),
  );

  const hasFailingChecklist = Object.values(checklistResults).some(
    (r) => r === 'fail',
  );

  const handleApproveDocument = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const slug = getStepSlug();

      // Build checklist evidence
      const sections = {};
      for (const [sectionKey, section] of Object.entries(CHECKLIST_TEMPLATE)) {
        sections[sectionKey] = {
          title: section.title,
          items: section.items.map((item) => ({
            id: item.id,
            text: item.text,
            result: checklistResults[item.id] || 'na',
            notes: checklistNotes[item.id] || '',
          })),
        };
      }

      const payload = {
        slug,
        comment: approvalJustification || 'Training exercise approval',
        checklist: {
          frm_dc_004_version: '1.0',
          sections,
          outcome: {
            decision: hasFailingChecklist ? 'fail' : 'pass',
            reviewer_signature: 'submitted via training UI',
          },
        },
        approval_justification: {
          justification: approvalJustification,
          ai_assisted: aiAssisted,
          ai_disclosure: aiAssisted ? aiDisclosure : '',
        },
        change_classification: {
          classification: changeClassification,
          rationale: classificationRationale,
        },
      };

      const res = await fetch(`${getApiPath()}/@docs/draft/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status}`);
      }

      // Now validate steps 5 and 6
      await handleValidateStep(5);
      setTimeout(async () => {
        await handleValidateStep(6);
      }, 300);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to abandon this training session? This cannot be undone.')) {
      return;
    }
    setAbandoning(true);
    try {
      const res = await fetch(`${getApiPath()}/@qms/training/session/abandon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ session_id: parseInt(sessionId, 10) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status}`);
      }
      history.push('/qms/training');
    } catch (err) {
      setError(err.message);
      setAbandoning(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading training session..." />
      </Container>
    );
  }

  if (!session) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message negative content={error || 'Session not found'} />
        <Link to="/qms/training">&larr; Back to Training</Link>
      </Container>
    );
  }

  // Completed view
  if (session.session_status === 'completed') {
    return (
      <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: 800 }}>
        <Helmet title="Training Complete" />
        <Link to="/qms/training">&larr; Back to Training</Link>

        <Segment textAlign="center" color="green" padded="very" style={{ marginTop: '1em' }}>
          <Icon name="trophy" size="huge" color="yellow" />
          <Header as="h1" color="green">
            Congratulations!
            <Header.Subheader>
              You have completed {session.module_title}
            </Header.Subheader>
          </Header>
          <p style={{ fontSize: '1.2em' }}>
            You are now qualified as <strong>{(session.grants_roles || []).join(' and ')}</strong> in
            the QMS.
          </p>
          {session.certificate_id && (
            <Button
              as={Link}
              to={`/qms/training/certificate/${encodeURIComponent(session.certificate_id)}`}
              color="blue"
              size="large"
              style={{ marginTop: '1em' }}
            >
              <Icon name="certificate" /> View Certificate
            </Button>
          )}
        </Segment>
      </Container>
    );
  }

  // Abandoned view
  if (session.session_status === 'abandoned') {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Training Abandoned" />
        <Link to="/qms/training">&larr; Back to Training</Link>
        <Message warning>
          <Message.Header>Session Abandoned</Message.Header>
          <p>This training session was abandoned. You can start a new one from the training dashboard.</p>
        </Message>
      </Container>
    );
  }

  // In-progress view: the guided wizard
  const renderStepAction = (stepNum) => {
    const stepDef = steps.find((s) => s.step === stepNum);
    if (!stepDef) return null;

    // Steps 1-2: Edit the doc, then check progress
    if (stepNum <= 2) {
      return (
        <div>
          <Button
            as={Link}
            to={`/docs/${getStepSlug()}/edit`}
            color="blue"
            style={{ marginRight: '1em' }}
          >
            <Icon name="edit" /> Open Document Editor
          </Button>
          <Button
            color="teal"
            loading={validating}
            onClick={() => handleValidateStep(stepNum)}
          >
            <Icon name="check circle" /> Check Progress
          </Button>
        </div>
      );
    }

    // Steps 3-4: Submit with COI + qualification
    if (stepNum === 3 || stepNum === 4) {
      if (stepNum === 4) {
        // Step 4 is auto-validated after step 3 submission
        return (
          <div>
            <p>This step validates automatically after submission in Step 3.</p>
            <Button
              color="teal"
              loading={validating}
              onClick={() => handleValidateStep(stepNum)}
            >
              <Icon name="check circle" /> Validate
            </Button>
          </div>
        );
      }

      return (
        <Form>
          <Header as="h4">
            <Icon name="shield" /> Conflict of Interest Declaration
          </Header>

          <Form.Field>
            <Checkbox
              label="I have a conflict of interest to declare"
              checked={coiHasConflict}
              onChange={(e, { checked }) => setCoiHasConflict(checked)}
            />
          </Form.Field>

          {coiHasConflict && (
            <Form.Field>
              <label>Describe the conflict</label>
              <TextArea
                value={coiDeclaration}
                onChange={(e) => setCoiDeclaration(e.target.value)}
                placeholder="Describe the nature of the conflict..."
                rows={2}
              />
            </Form.Field>
          )}

          <Form.Field>
            <Checkbox
              label="Micro-enterprise provision applies (same person may author and review)"
              checked={microEnterprise}
              onChange={(e, { checked }) => setMicroEnterprise(checked)}
            />
          </Form.Field>

          <Divider />

          <Header as="h4">
            <Icon name="user" /> Qualification
          </Header>

          <Form.Field required>
            <label>Qualification basis for reviewing this document</label>
            <TextArea
              value={qualificationBasis}
              onChange={(e) => setQualificationBasis(e.target.value)}
              placeholder="State your basis for being qualified to review this document (e.g., role, training, experience)..."
              rows={2}
            />
          </Form.Field>

          <Divider />

          <Button
            color="green"
            loading={submitting}
            disabled={!qualificationBasis.trim()}
            onClick={handleSubmitForReview}
          >
            <Icon name="send" /> Submit for Review
          </Button>
        </Form>
      );
    }

    // Steps 5-6: Checklist + approve
    if (stepNum === 5 || stepNum === 6) {
      if (stepNum === 6) {
        return (
          <div>
            <p>This step validates automatically after approval in Step 5.</p>
            <Button
              color="teal"
              loading={validating}
              onClick={() => handleValidateStep(stepNum)}
            >
              <Icon name="check circle" /> Validate
            </Button>
          </div>
        );
      }

      return (
        <Form>
          {/* FRM-DC-004 Checklist */}
          <Header as="h4">
            <Icon name="tasks" /> FRM-DC-004 Self-Review Checklist
          </Header>
          {Object.entries(CHECKLIST_TEMPLATE).map(([sectionKey, section]) => (
            <Segment key={sectionKey} style={{ marginBottom: '1em' }}>
              <Header as="h5">{section.title}</Header>
              <Table compact size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={1}>ID</Table.HeaderCell>
                    <Table.HeaderCell width={8}>Check Item</Table.HeaderCell>
                    <Table.HeaderCell width={3}>Result</Table.HeaderCell>
                    <Table.HeaderCell width={4}>Notes</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {section.items.map((item) => (
                    <Table.Row
                      key={item.id}
                      negative={checklistResults[item.id] === 'fail'}
                      positive={checklistResults[item.id] === 'pass'}
                    >
                      <Table.Cell><strong>{item.id}</strong></Table.Cell>
                      <Table.Cell>{item.text}</Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          selection
                          compact
                          options={RESULT_OPTIONS}
                          value={checklistResults[item.id] || ''}
                          placeholder="..."
                          onChange={(e, { value }) =>
                            setChecklistResults((prev) => ({ ...prev, [item.id]: value }))
                          }
                          style={{ minWidth: '80px' }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <input
                          type="text"
                          value={checklistNotes[item.id] || ''}
                          onChange={(e) =>
                            setChecklistNotes((prev) => ({ ...prev, [item.id]: e.target.value }))
                          }
                          placeholder="Optional"
                          style={{
                            width: '100%', border: '1px solid #ddd',
                            padding: '4px 6px', borderRadius: '3px',
                          }}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          ))}

          {hasFailingChecklist && (
            <Message negative>
              <Icon name="exclamation triangle" />
              One or more checklist items failed. All must pass or be N/A.
            </Message>
          )}

          {!allChecklistAnswered && (
            <Message warning>
              <Icon name="exclamation triangle" />
              All checklist items must be answered before approval.
            </Message>
          )}

          <Divider />

          {/* Change Classification */}
          <Header as="h4"><Icon name="tag" /> Change Classification</Header>
          <Form.Field>
            <label>Classification</label>
            <Dropdown
              selection
              options={CLASSIFICATION_OPTIONS}
              value={changeClassification}
              onChange={(e, { value }) => setChangeClassification(value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Rationale</label>
            <TextArea
              value={classificationRationale}
              onChange={(e) => setClassificationRationale(e.target.value)}
              placeholder="Why this classification level?"
              rows={2}
            />
          </Form.Field>

          <Divider />

          {/* Approval Justification */}
          <Header as="h4"><Icon name="check circle" /> Approval Justification</Header>
          <Form.Field required>
            <label>Justification</label>
            <TextArea
              value={approvalJustification}
              onChange={(e) => setApprovalJustification(e.target.value)}
              placeholder="Why is this document being approved?"
              rows={3}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              label="AI tools were used in preparing or reviewing this document"
              checked={aiAssisted}
              onChange={(e, { checked }) => setAiAssisted(checked)}
            />
          </Form.Field>
          {aiAssisted && (
            <Form.Field>
              <label>AI Disclosure</label>
              <TextArea
                value={aiDisclosure}
                onChange={(e) => setAiDisclosure(e.target.value)}
                placeholder="Describe how AI tools were used..."
                rows={2}
              />
            </Form.Field>
          )}

          <Divider />

          <Button
            color="green"
            loading={submitting}
            disabled={!allChecklistAnswered || hasFailingChecklist || !approvalJustification.trim()}
            onClick={handleApproveDocument}
          >
            <Icon name="check" /> Approve Document
          </Button>
        </Form>
      );
    }

    return null;
  };

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title={`Training — ${session.module_title}`} />
      <Link to="/qms/training">&larr; Back to Training</Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        <Icon name="graduation cap" />
        <Header.Content>
          {session.module_title}
          <Header.Subheader>
            {session.sop_reference} | Document: {session.training_doc_id}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {validationMsg && (
        <Message
          positive={validationMsg.positive}
          negative={!validationMsg.positive}
          onDismiss={() => setValidationMsg(null)}
        >
          <p>{validationMsg.text}</p>
        </Message>
      )}

      <Grid stackable>
        {/* Step list (left) */}
        <Grid.Column width={4}>
          <Step.Group vertical fluid size="small">
            {steps.map((s) => {
              const isCompleted = completedNums.has(s.step);
              const isCurrent = s.step === currentStep;
              return (
                <Step
                  key={s.step}
                  active={isCurrent}
                  completed={isCompleted}
                  disabled={!isCompleted && !isCurrent}
                >
                  <Icon
                    name={
                      isCompleted ? 'check circle' : isCurrent ? 'circle' : 'circle outline'
                    }
                    color={isCompleted ? 'green' : isCurrent ? 'blue' : 'grey'}
                  />
                  <Step.Content>
                    <Step.Title>{s.title}</Step.Title>
                  </Step.Content>
                </Step>
              );
            })}
          </Step.Group>

          <Button
            basic
            color="red"
            size="small"
            loading={abandoning}
            onClick={handleAbandon}
            style={{ marginTop: '1em' }}
          >
            <Icon name="trash" /> Abandon Session
          </Button>
        </Grid.Column>

        {/* Instructions + action (right) */}
        <Grid.Column width={12}>
          {steps.map((s) => {
            if (s.step !== currentStep) return null;
            return (
              <div key={s.step}>
                <Segment>
                  <Label attached="top left" color="blue">
                    Step {s.step} of {steps.length}
                  </Label>
                  <Header as="h2" style={{ marginTop: '0.5em' }}>
                    {s.title}
                  </Header>
                  <p style={{ fontSize: '1.1em', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                    {s.instructions}
                  </p>
                </Segment>

                <Segment>
                  {renderStepAction(s.step)}
                </Segment>
              </div>
            );
          })}
        </Grid.Column>
      </Grid>
    </Container>
  );
}
